import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSSelectors: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Selectors &amp; Traversal</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Selectors answer &quot;which element?&quot; Traversal answers &quot;where can I move from here?&quot; Together they&apos;re the foundation of every DOM interaction.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="14 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine building Amazon. When a user clicks <Mark>Add To Cart</Mark>, JavaScript must find the product card, read the product id, find the cart counter, and update it.</Para>
   <Para>How does JavaScript locate specific elements among thousands in the DOM? Without selectors there&apos;s no way to find elements, update the UI, or attach events. Selectors solve this.</Para>
  </section>

  {/* 2. What Are Selectors */}
  <section className="mb-12">
   <SectionHeading n={2}>What Are Selectors?</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Selectors</Mark> are APIs that let JavaScript locate elements inside the DOM tree.</p>
   </div>
   <Para>If the DOM is a huge city, selectors are the GPS. Without GPS you can&apos;t find anything; without selectors JavaScript can&apos;t interact with the page.</Para>
  </section>

  {/* 3. DOM Tree Refresher */}
  <section className="mb-12">
   <SectionHeading n={3}>DOM Tree Refresher</SectionHeading>
   <CodeBlock snippets={[{ language: 'html', code: `<body>\n  <div id="container">\n    <h1 class="title">Welcome</h1>\n    <button id="loginBtn">Login</button>\n  </div>\n</body>` }]} defaultLanguage="html" id="sel-html" />
   <CodeBlock snippets={[{ language: 'text', code: `Document\n└── body\n    └── div#container\n        ├── h1.title\n        └── button#loginBtn` }]} defaultLanguage="text" id="sel-tree" />
   <Para>Selectors navigate this tree.</Para>
  </section>

  {/* 4. getElementById */}
  <section className="mb-12">
   <SectionHeading n={4}>getElementById()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const button = document.getElementById("loginBtn"); // single element` }]} defaultLanguage="javascript" id="sel-byid" />
   <Para><SubHeading>Why is it fast?</SubHeading> The browser maintains an internal <Mark>ID lookup table</Mark>, so the lookup is extremely fast. Common for forms, modals, unique buttons, and root containers.</Para>
  </section>

  {/* 5. getElementsByClassName */}
  <section className="mb-12">
   <SectionHeading n={5}>getElementsByClassName()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const cards = document.getElementsByClassName("product-card"); // HTMLCollection` }]} defaultLanguage="javascript" id="sel-byclass" />
   <Callout tone="info" title="It's LIVE">Returns an <Code>HTMLCollection</Code> (not an array). It&apos;s <Mark>live</Mark> — when the DOM changes, the collection updates automatically.</Callout>
  </section>

  {/* 6. getElementsByTagName */}
  <section className="mb-12">
   <SectionHeading n={6}>getElementsByTagName()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const buttons = document.getElementsByTagName("button"); // HTMLCollection` }]} defaultLanguage="javascript" id="sel-bytag" />
   <Para>Also returns a live <Code>HTMLCollection</Code> — rarely used directly today.</Para>
  </section>

  {/* 7. querySelector */}
  <section className="mb-12">
   <SectionHeading n={7}>querySelector()</SectionHeading>
   <Para>The modern, most-used selector. It uses CSS syntax and returns the <Mark>first match only</Mark>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `document.querySelector("#loginBtn");\ndocument.querySelector(".card");\ndocument.querySelector("button");\ndocument.querySelector("div > button");\n\n// nested CSS selectors work too\ndocument.querySelector(".product-card .price");` }]} defaultLanguage="javascript" id="sel-qs" />
  </section>

  {/* 8. querySelectorAll */}
  <section className="mb-12">
   <SectionHeading n={8}>querySelectorAll()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const cards = document.querySelectorAll(".product-card"); // NodeList` }]} defaultLanguage="javascript" id="sel-qsa" />
   <Callout tone="info" title="It's STATIC">Returns a <Code>NodeList</Code> (not an <Code>HTMLCollection</Code>). It&apos;s <Mark>static</Mark> — later DOM changes don&apos;t update it.</Callout>
  </section>

  {/* 9. HTMLCollection vs NodeList */}
  <section className="mb-12">
   <SectionHeading n={9}>HTMLCollection vs NodeList</SectionHeading>
   <TwoUp
    left={{ title: 'HTMLCollection', items: ['Live — updates automatically', 'From getElementsBy* methods', 'No array methods (no forEach)'] }}
    right={{ title: 'NodeList', items: ['Usually static', 'From querySelectorAll', 'Has forEach()'] }}
   />
   <Para>Interview answer: <Mark>HTMLCollection is live; NodeList is usually static.</Mark></Para>
  </section>

  {/* 10. Parent Traversal */}
  <section className="mb-12">
   <SectionHeading n={10}>Parent Traversal</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const button = document.querySelector("#loginBtn");\nconsole.log(button.parentElement); // moves upward` }]} defaultLanguage="javascript" id="sel-parent" />
   <Para>Production example: find a card → find its parent product → remove the product.</Para>
  </section>

  {/* 11. Child Traversal */}
  <section className="mb-12">
   <SectionHeading n={11}>Child Traversal</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const container = document.querySelector("#container");\nconsole.log(container.children); // HTMLCollection` }]} defaultLanguage="javascript" id="sel-children" />
   <Para>Useful for menus, tables, lists, and cards.</Para>
  </section>

  {/* 12 & 13. first / last child */}
  <section className="mb-12">
   <SectionHeading n={12}>firstElementChild &amp; lastElementChild</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.firstElementChild;\nelement.lastElementChild;` }]} defaultLanguage="javascript" id="sel-firstlast" />
  </section>

  {/* 14. nextElementSibling */}
  <section className="mb-12">
   <SectionHeading n={14}>nextElementSibling</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// <input><span>Error</span>\ninput.nextElementSibling; // the Error span` }]} defaultLanguage="javascript" id="sel-next" />
  </section>

  {/* 15. previousElementSibling */}
  <section className="mb-12">
   <SectionHeading n={15}>previousElementSibling</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.previousElementSibling; // moves backward` }]} defaultLanguage="javascript" id="sel-prev" />
  </section>

  {/* 16. closest */}
  <section className="mb-12">
   <SectionHeading n={16}>closest()</SectionHeading>
   <Para>One of the most useful traversal APIs — it moves <Mark>upward</Mark> until it finds a matching ancestor:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.closest(".product-card");` }]} defaultLanguage="javascript" id="sel-closest" />
   <Para>Production example (Amazon): click Add To Cart → <Code>closest(".product-card")</Code> → read the product id.</Para>
  </section>

  {/* 17. matches */}
  <section className="mb-12">
   <SectionHeading n={17}>matches()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.matches(".active"); // true / false` }]} defaultLanguage="javascript" id="sel-matches" />
   <Para>Checks whether an element satisfies a selector — essential for <Mark>event delegation</Mark>.</Para>
  </section>

  {/* 18. Shopping cart example */}
  <section className="mb-12">
   <SectionHeading n={18}>Real Production Example: Shopping Cart</SectionHeading>
   <CodeBlock snippets={[{ language: 'html', code: `<div class="product-card" data-id="123">\n  <button class="add-btn">Add</button>\n</div>` }]} defaultLanguage="html" id="sel-cart-html" />
   <CodeBlock snippets={[{ language: 'javascript', code: `// click Add → find card → read product id → update cart\nconst id = button.closest(".product-card").dataset.id; // "123"` }]} defaultLanguage="javascript" id="sel-cart-js" />
  </section>

  {/* 19. Performance Considerations */}
  <section className="mb-12">
   <SectionHeading n={19}>Performance Considerations</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `getElementById()  →  querySelector()  →  complex CSS queries\n(fastest)                                  (slowest)` }]} defaultLanguage="text" id="sel-perf" />
   <Callout tone="warning" title="Cache references">
    Don&apos;t repeatedly call <Code>querySelector()</Code> inside loops — look the element up once and reuse the reference.
   </Callout>
  </section>

  {/* 20-23. Common Interview Questions */}
  <section className="mb-12">
   <SectionHeading n={20}>Common Interview Questions</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['getElementById vs querySelector?', 'HTMLCollection vs NodeList?', 'What does closest() do?', 'How does DOM traversal work?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="getElementById vs querySelector — which to use?">
    <Code>getElementById</Code> is fastest (uses an internal ID table) but only matches by id. <Code>querySelector</Code> accepts any CSS selector and is more flexible, returning the first match. Use the former for known unique ids, the latter for everything else.
   </QA>
   <QA q="HTMLCollection vs NodeList?">
    <Code>HTMLCollection</Code> (from <Code>getElementsBy*</Code>) is live and lacks array methods. <Code>NodeList</Code> (from <Code>querySelectorAll</Code>) is usually static but supports <Code>forEach</Code>. Convert either to an array with <Code>Array.from()</Code> when you need full array methods.
   </QA>
   <QA q="What does closest() do?">
    Starting from the element itself, it walks up the ancestor chain and returns the first element matching the selector (or <Code>null</Code>). It&apos;s ideal for finding a container from a clicked child, especially in event delegation.
   </QA>
  </section>

  {/* Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={21}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Selectors locate elements inside the DOM, while traversal APIs navigate the relationships between parent, child, and sibling elements.
    </p>
   </div>
  </section>

  {/* Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={22}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Selectors answer &quot;which element?&quot; Traversal answers &quot;where can I move from here?&quot; Together they form the foundation of all DOM interactions, event handling, and modern frontend development.
   </Callout>
  </section>
 </div>
);

export default JSSelectors;
